// Blok „Moje lhůty a převzetí“ na Domů (F10 10.1; prototyp dlaždice „PŘEVZETÍ K PODPISU“ H:43429, „ÚRAZY K POTVRZENÍ“
// pro admina a jednatele, přehled lhůt zaměstnance): z `GET /ja/karta` ukáže lhůty, které nejsou v pořádku (stav ze
// serveru), a tlačítko „Převzetí k podpisu (N)“ → modal s podpisem na tabletu; admin a jednatel navíc dlaždici
// „Úrazy k potvrzení“ (`GET /urazy?stav=ceka_potvrzeni`) s odkazem do karty postiženého. Převzetí, které kancelář
// za posledních 14 dní zapsala jako „podepsané na papíře“ (05/236), zaměstnanec vidí jako informaci — k podpisu
// už mu nepřijde.
import type { Ja } from '@ecoser/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  AKCE_CIST,
  AKCE_VLASTNI,
  tridaStavuLhuty,
  useJaKarta,
  useKnihaUrazu,
} from '../api/karta-zamestnance';
import { formatDatum } from '../formaty';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import '../sekce/zamestnanci/karta/karta.css';
import { smiOtevritOdkaz } from './odkazy';

/** Kniha úrazů za celou firmu (záložka v Zaměstnancích, W66 díl 4). */
const ODKAZ_KNIHA_URAZU = '/zamestnanci?zalozka=urazy';

/** Kolik dní zpátky ukázat převzetí zapsané kanceláří jako podepsané na papíře. */
const DNI_NA_PAPIRE = 14;

// Modal s podpisovým plátnem se stahuje až při otevření (plátno do hlavního balíku nepatří).
const PrevzetiKPodpisu = lina(() => import('./PrevzetiKPodpisu').then((m) => m.PrevzetiKPodpisu));

export function MojeKarta({ ja }: { ja: Ja }) {
  const akce = new Set(ja.akce ?? []);
  const smiVlastni = akce.has(AKCE_VLASTNI);
  const smiUrazy = akce.has(AKCE_CIST);
  const karta = useJaKarta(smiVlastni);
  const urazy = useKnihaUrazu('ceka_potvrzeni', smiUrazy);
  const [otevreno, setOtevreno] = useState(false);
  if (!smiVlastni) return null;

  const kPodpisu = karta.data?.k_podpisu ?? [];
  const lhutyKReseni = (karta.data?.lhuty ?? []).filter(
    (l) => l.stav !== 'ok' && l.stav !== 'vypnuto',
  );
  const urazyKPotvrzeni = urazy.data?.polozky ?? [];
  const od = Date.now() - DNI_NA_PAPIRE * 86_400_000;
  const naPapire = [...(karta.data?.oopp ?? []), ...(karta.data?.sverene_veci ?? [])].filter(
    (v) => v.na_papire && v.podepsano_kdy && Date.parse(v.podepsano_kdy) >= od,
  );
  if (!karta.data && !urazy.data) return null;
  if (
    kPodpisu.length === 0 &&
    lhutyKReseni.length === 0 &&
    urazyKPotvrzeni.length === 0 &&
    naPapire.length === 0
  )
    return null;

  return (
    <section
      className="karta domu__karta"
      id="moje-karta"
      aria-labelledby="moje-karta-titul"
      data-testid="moje-karta"
    >
      <div className="domu__karta-hlavicka">
        <h2 id="moje-karta-titul" className="domu__karta-titul">
          Moje lhůty a převzetí
        </h2>
      </div>
      {kPodpisu.length > 0 && (
        <div className="hlaska hlaska--warn" role="status" data-testid="prevzeti-dlazdice">
          <strong>Převzetí k podpisu: {kPodpisu.length}</strong> —{' '}
          {kPodpisu
            .map((p) => p.nazev)
            .slice(0, 3)
            .join(', ')}
          {kPodpisu.length > 3 ? '…' : ''}
          <button
            type="button"
            className="tlacitko tlacitko--primarni tlacitko--radek"
            onClick={() => setOtevreno(true)}
            data-testid="otevrit-prevzeti"
          >
            Podepsat převzetí
          </button>
        </div>
      )}
      {naPapire.length > 0 && (
        <div className="hlaska" role="status" data-testid="prevzeti-na-papire">
          Převzetí podepsané na papíře zapsala kancelář (výdejní list je v kartě):{' '}
          {naPapire.map((v) => v.nazev).join(', ')}.
        </div>
      )}
      {urazyKPotvrzeni.length > 0 && (
        <div className="hlaska hlaska--danger" role="status" data-testid="urazy-dlazdice">
          <strong>Úrazy k potvrzení: {urazyKPotvrzeni.length}</strong> — zápisy do knihy úrazů od
          zaměstnanců.{' '}
          {urazyKPotvrzeni.slice(0, 3).map((u) => {
            const cesta = `/zamestnanci/${u.zamestnanec_id}/urazy`;
            // Účet bez sekce Zaměstnanci vidí jméno, ale odkaz do karty ne.
            return smiOtevritOdkaz(ja, cesta) ? (
              <Link key={u.id} to={cesta} className="tlacitko tlacitko--textove">
                {u.zamestnanec_jmeno}
              </Link>
            ) : (
              <span key={u.id}>{u.zamestnanec_jmeno} </span>
            );
          })}
          {/* Všechny zápisy za celou firmu (W66 díl 4): kniha úrazů v Zaměstnancích; bez sekce se odkaz nevykreslí. */}
          {smiOtevritOdkaz(ja, ODKAZ_KNIHA_URAZU) && (
            <Link to={ODKAZ_KNIHA_URAZU} className="tlacitko tlacitko--textove">
              Kniha úrazů
            </Link>
          )}
        </div>
      )}
      {lhutyKReseni.length > 0 && (
        <ul className="moje-karta__lhuty" data-testid="moje-lhuty">
          {lhutyKReseni.map((l) => (
            <li key={l.kod} className="moje-karta__lhuta" data-testid={`moje-lhuta-${l.kod}`}>
              <strong>{l.nazev}</strong>
              {l.platnost_do && <span className="podtitul">do {formatDatum(l.platnost_do)}</span>}
              <span className={tridaStavuLhuty(l.stav)}>{l.stav_popis}</span>
            </li>
          ))}
        </ul>
      )}
      {otevreno && karta.data && (
        <NacitaniCasti>
          <PrevzetiKPodpisu karta={karta.data} naZavrit={() => setOtevreno(false)} />
        </NacitaniCasti>
      )}
    </section>
  );
}
